import React from 'react';
import { BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущих значений
  const revenue = parseFloat(formData.revenue) || 0;
  const otherIncome = parseFloat(formData.otherIncome) || 0;
  const totalIncome = revenue + otherIncome;
  
  const expensesRelated = parseFloat(formData.expensesRelated) || 0;
  const expensesOther = parseFloat(formData.expensesOther) || 0;
  const totalExpenses = expensesRelated + expensesOther;
  
  // Обработчик изменения выручки
  const handleRevenueChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      revenue: newValue
    }));
  };
  
  // Обработчик изменения расходов
  const handleExpensesChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      expensesRelated: newValue
    }));
  };
  
  // Предопределенные размеры выручки для быстрого выбора
  const predefinedRevenue = [
    { value: '1000000', label: '1 млн' },
    { value: '10000000', label: '10 млн' },
    { value: '50000000', label: '50 млн' },
    { value: '100000000', label: '100 млн' }
  ];
  
  // Предопределенные соотношения расходов к выручке
  const predefinedExpensesRatios = [
    { value: 0.5, label: '50%' },
    { value: 0.7, label: '70%' },
    { value: 0.8, label: '80%' },
    { value: 0.9, label: '90%' }
  ];
  
  // Функция для установки расходов как процента от выручки
  const setExpensesRatio = (ratio) => {
    const newExpenses = Math.round(revenue * ratio);
    setFormData(prev => ({
      ...prev,
      expensesRelated: newExpenses.toString()
    }));
  };
  
  // Функция быстрого применения IT-льготы
  const applyITBenefits = () => {
    setFormData(prev => ({
      ...prev,
      specialTaxRegimes: true,
      isITCompany: true
    }));
  };
  
  // Функция быстрого применения вычета на НИОКР
  const applyRnDBenefits = () => {
    // Предположим, что 10% от расходов - это НИОКР
    const rndAmount = Math.round(expensesRelated * 0.1);
    setFormData(prev => ({
      ...prev,
      specialDeductions: true,
      researchAndDevelopment: rndAmount.toString()
    }));
  };
  
  // Функция расчета
  const calculateTax = () => {
    // Эмулируем нажатие кнопки расчета из основной формы
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Выручка от реализации */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="revenueSlider" className="text-gray-600 dark:text-gray-400">Выручка от реализации</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(revenue)}</span>
          </div>
          <input
            type="range"
            id="revenueSlider"
            min="1000000"
            max="200000000"
            step="1000000"
            value={revenue}
            onChange={handleRevenueChange}
            aria-label="Выручка от реализации"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами выручки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedRevenue.map(item => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, revenue: item.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.revenue === item.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Расходы, связанные с производством и реализацией */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="expensesSlider" className="text-gray-600 dark:text-gray-400">Расходы, связанные с реализацией</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(expensesRelated)} 
              {revenue > 0 && ` (${Math.round(expensesRelated / revenue * 100)}%)`}
            </span>
          </div>
          <input
            type="range"
            id="expensesSlider"
            min="0"
            max={revenue * 1.2}
            step="100000"
            value={expensesRelated}
            onChange={handleExpensesChange}
            aria-label="Расходы, связанные с производством и реализацией"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными процентами расходов */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedExpensesRatios.map(ratio => (
              <button
                key={ratio.value}
                type="button"
                onClick={() => setExpensesRatio(ratio.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.abs(expensesRelated / revenue - ratio.value) < 0.01 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {ratio.label} расходов
              </button>
            ))}
          </div>
        </div>
        
        {/* Упрощенный выбор налогового региона */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Регион
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, taxRegion: 'default' }))}
              className={`p-2 text-xs rounded-lg border transition-colors text-center
                ${formData.taxRegion === 'default' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Стандартная<br/>ставка (20%)
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, taxRegion: 'moscow' }))}
              className={`p-2 text-xs rounded-lg border transition-colors text-center
                ${formData.taxRegion === 'moscow' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Москва<br/>(20%)
            </button>
          </div>
        </div>
        
        {/* Упрощенный выбор льгот */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Быстрое применение льгот
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={applyITBenefits}
              className={`p-2 text-xs rounded-lg border transition-colors text-center
                ${formData.isITCompany 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              IT-компания<br/>(льгота)
            </button>
            <button
              type="button"
              onClick={applyRnDBenefits}
              className={`p-2 text-xs rounded-lg border transition-colors text-center
                ${formData.specialDeductions && parseFloat(formData.researchAndDevelopment) > 0
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              НИОКР<br/>(вычет)
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTax}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating}
          isLoading={isCalculating}
        >
          Рассчитать налог
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;